import { ExternalLink, Github, GitBranch, GitCommit, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RollbackButton } from "@/components/RollbackButton";
import { cn } from "@/lib/utils";
import { DeploymentScreenshot } from "./DeploymentScreenshot";
import { LiveStatusDot } from "./LiveStatusDot";
import { VisitMenu } from "./VisitMenu";

export interface ActiveProductionDeployment {
  id: string;
  status: string;
  commitSha: string;
  commitMessage: string | null;
  commitAuthor: string | null;
  branch: string;
  env: string;
  envResolvedFrom: string;
  createdAt: string;
  readyAt: string | null;
  durationMs: number | null;
  screenshotAt: string | null;
}

export interface AttachedDomain {
  id: string;
  hostname: string;
  displayHostname: string | null;
}

interface Props {
  orgSlug: string;
  projectSlug: string;
  deployment: ActiveProductionDeployment;
  attachedDomains: AttachedDomain[];
  platformPreviewUrl: string;
  githubRepoUrl: string | null;
  /** Most-recent ready production deployment that is NOT the active one; powers single-click Rollback. */
  previousProductionDeploymentId: string | null;
}

const STATUS_TONE: Record<string, string> = {
  ready: "bg-success-subtle text-success border-success/30",
  building: "bg-attention-subtle text-attention border-attention/30",
  queued: "bg-attention-subtle text-attention border-attention/30",
  failed: "bg-danger-subtle text-danger border-danger/30",
  cancelled: "bg-subtle text-muted-foreground border-border-muted",
  stopped: "bg-subtle text-muted-foreground border-border-muted",
};

/**
 * v4 (004-project-overview-tab) — the Production Deployment card.
 *
 * Header: title + (action button placeholders for US3) + LiveStatusDot.
 * Body: two-column grid — left column is the thumbnail placeholder
 * (DeploymentScreenshot lands in US2 / Phase 4 and replaces this), right
 * column is the metadata stack.
 *
 * Null-graceful: commitMessage and commitAuthor are nullable. When
 * commitAuthor is null, falls back to "via <envResolvedFrom>" so the row
 * always shows trigger context.
 */
export function ProductionCard({
  orgSlug,
  projectSlug,
  deployment,
  attachedDomains,
  platformPreviewUrl,
  githubRepoUrl,
  previousProductionDeploymentId,
}: Props) {
  const deployedDisplay = deployment.commitAuthor
    ? `by ${deployment.commitAuthor}`
    : `via ${deployment.envResolvedFrom}`;

  // Primary URL precedence (mirrors server-side platform-url helper):
  // first attached custom domain alphabetical, else platform preview URL.
  const sortedDomains = [...attachedDomains].sort((a, b) => a.hostname.localeCompare(b.hostname));
  const primaryUrl =
    sortedDomains.length > 0 ? `https://${sortedDomains[0].hostname}` : platformPreviewUrl;

  return (
    <div className="of-card overflow-hidden">
      <div className="of-card-header flex flex-wrap items-center justify-between gap-3 gap-y-2 px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Production Deployment</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {githubRepoUrl && (
            <Button asChild variant="default" size="sm">
              <a href={githubRepoUrl} target="_blank" rel="noreferrer">
                <Github className="h-3.5 w-3.5" />
                Repository
              </a>
            </Button>
          )}
          <RollbackButton
            orgSlug={orgSlug}
            projectSlug={projectSlug}
            env="production"
            targetDeploymentId={previousProductionDeploymentId ?? ""}
            disabled={!previousProductionDeploymentId}
            label="Instant Rollback"
          />
          <VisitMenu primaryUrl={primaryUrl} attachedDomains={attachedDomains} />
          <LiveStatusDot orgSlug={orgSlug} projectSlug={projectSlug} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {/* Left column: thumbnail captured by the screenshot worker. */}
        <div className="border-b md:border-b-0 md:border-r border-border-muted aspect-[16/10] overflow-hidden">
          <DeploymentScreenshot
            orgSlug={orgSlug}
            projectSlug={projectSlug}
            deploymentId={deployment.id}
            screenshotAt={deployment.screenshotAt}
            label={`${deployment.branch} · ${deployment.commitSha}`}
          />
        </div>
        {/* Right column: metadata stack. */}
        <div className="p-4 space-y-3 min-w-0">
          <Field label="Deployment">
            <a
              href={platformPreviewUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs hover:underline inline-flex items-center gap-1 min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-normal break-all sm:whitespace-nowrap"
            >
              {platformPreviewUrl.replace(/^https?:\/\//, "")}
              <ExternalLink className="h-3 w-3" />
            </a>
          </Field>
          <Field
            label={
              <span className="inline-flex items-center gap-1">
                Domains
                <Link
                  to={`/orgs/${orgSlug}/projects/${projectSlug}/domains`}
                  className="text-subtle-foreground hover:text-foreground"
                  title="Add domain"
                >
                  <Plus className="h-3 w-3" />
                </Link>
              </span>
            }
          >
            {attachedDomains.length === 0 ? (
              <span className="text-2xs text-subtle-foreground">none attached</span>
            ) : (
              <div className="flex flex-wrap gap-2">
                {attachedDomains.map((d) => (
                  <a
                    key={d.id}
                    href={`https://${d.hostname}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-xs hover:underline inline-flex items-center gap-1 min-w-0 max-w-full break-all"
                  >
                    {d.displayHostname ?? d.hostname}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>
            )}
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Status">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-2xs uppercase tracking-wider border rounded px-1.5 py-0.5 font-mono",
                  STATUS_TONE[deployment.status] ?? STATUS_TONE.cancelled,
                )}
              >
                {deployment.status}
              </span>
            </Field>
            <Field label="Created">
              <span className="text-xs">
                {relative(deployment.createdAt)} {deployedDisplay}
              </span>
            </Field>
          </div>
          <Field label="Source">
            <div className="space-y-1 text-xs">
              <div className="inline-flex items-center gap-1.5">
                <GitBranch className="h-3 w-3 text-subtle-foreground" />
                <span className="font-mono">{deployment.branch}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 min-w-0 max-w-full overflow-hidden">
                <GitCommit className="h-3 w-3 text-subtle-foreground shrink-0" />
                <span className="font-mono shrink-0">{deployment.commitSha}</span>
                {deployment.commitMessage && (
                  <span className="text-muted-foreground truncate min-w-0" title={deployment.commitMessage}>
                    {deployment.commitMessage}
                  </span>
                )}
              </div>
            </div>
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-2xs uppercase tracking-wider text-subtle-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
